import { MutableRefObject, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Option, isNone, isSome, none } from 'fp-ts/lib/Option';

import { ErrorData } from 'domain/types';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutType } from 'domain/entity/layout';
import { ReadWidgetData } from 'use-case/widgets/data/read/readWidgetDataUseCase';
import { CriteriaCallback, useCase } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useBranding } from 'adapter/in/common/context/static/Branding';
import { useIntersect } from 'adapter/in/common/hooks/useIntersect';
import { WidgetCriteria, getWidgetBuilder } from 'adapter/in/common/widget/widgetBuilder';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import { useDataDependencies } from 'adapter/in/common/commonDependencies';
import { WidgetDataProxyContext } from 'use-case/widgets/data/read/readWidgetDataCriteria';

export type WidgetDataError = Option<ErrorData>;
export type WidgetDataResult = Option<WidgetDataTypes>;

export const useWidgetData = (
    readWidgetData: ReadWidgetData
): {
    isWidgetDataLoading: boolean;
    widgetData: WidgetDataResult;
    widgetDataError: WidgetDataError;
    readWidgetData: CriteriaCallback;
    reset: () => void;
} => {
    const { isLoading, data, error, execute, reset } = useCase(readWidgetData, false);

    return {
        isWidgetDataLoading: isLoading,
        widgetData: data,
        widgetDataError: error,
        readWidgetData: execute,
        reset,
    };
};

export const useWidgetBuilder = (
    widgetCriteria: WidgetCriteria,
    criteriaBuilder: (widgetId: string, widgetDataContext?: WidgetDataProxyContext) => any,
    onReadWidgetDataDependencesChange?: (lastTimeUpdated: MutableRefObject<number>) => boolean,
    triggerRefreshTimestamp?: number,
    widgetDataContext?: WidgetDataProxyContext
): {
    WidgetBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        isOnEdit?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement
    ) => ReactElement;
    lastTimeUpdated: MutableRefObject<number>;
    handleReadWidgetData: () => void;
    branding: Branding;
    hasDataError: boolean;
} => {
    const [specificWidgetData, setSpecificWidgetData] = useState<WidgetDataResult>(none);
    const { readWidgetData: readWidgetDataUseCase } = useDataDependencies();
    const { profile } = useProfile();
    const { branding } = useBranding();

    const { referencer, entry } = useIntersect({
        root: null,
        threshold: 0.2,
        rootMargin: '0px',
    });

    const lastTimeUpdated = useRef(0);
    const { isWidgetDataLoading, widgetData, widgetDataError: hasWidgetDataError, readWidgetData } = useWidgetData(readWidgetDataUseCase);

    const handleReadWidgetData = useCallback(
        () => widgetCriteria.id && readWidgetData(criteriaBuilder(widgetCriteria.id, widgetDataContext)),
        [widgetCriteria]
    );

    useEffect(() => {
        if (
            widgetCriteria.id &&
            entry.isIntersecting &&
            ((onReadWidgetDataDependencesChange && onReadWidgetDataDependencesChange(lastTimeUpdated)) ||
                !onReadWidgetDataDependencesChange)
        ) {
            lastTimeUpdated.current = new Date().getTime();
            handleReadWidgetData();
        }
    }, [widgetCriteria, entry, lastTimeUpdated, triggerRefreshTimestamp]);

    useEffect(() => {
        if (isSome(widgetData)) {
            setSpecificWidgetData(widgetData);
        }
    }, [widgetData]);

    return {
        WidgetBuilder: getWidgetBuilder(
            { id: widgetCriteria.id, optionFields: widgetCriteria.optionFields },
            isSome(specificWidgetData) ? specificWidgetData.value : {},
            isWidgetDataLoading || isNone(specificWidgetData),
            branding,
            profile.decimalSeparator,
            profile.thousandsSeparator,
            profile.dateFormat,
            profile.currencySymbolInLastPosition,
            referencer,
            isSome(hasWidgetDataError) ? hasWidgetDataError.value : undefined
        ),
        lastTimeUpdated,
        handleReadWidgetData,
        hasDataError: !!isSome(hasWidgetDataError),
        branding,
    };
};
